<template>
    <div class="q-pa-md">
        <q-table
            ref="viewportTable"
            :rows="managers"
            :columns="columns"
            row-key="id"
            :loading="loadingManagers"
            :filter="filter"
            v-model:pagination="pagination"
            :rows-per-page-options="rowsPerPageOptions"
            flat
            bordered
        >
            <template v-slot:top-right>
                <q-input dense debounce="300" v-model="filter" placeholder="Search">
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </template>

            <template v-slot:body-cell-role="props">
                <q-td :props="props">
                    <q-badge :color="props.row.role === 'admin' ? 'deep-purple' : 'teal'" outline>
                        {{ roleLabel(props.row.role) }}
                    </q-badge>
                </q-td>
            </template>

            <template v-slot:body-cell-is_active="props">
                <q-td :props="props">
                    <q-badge :color="props.row.is_active ? 'positive' : 'grey-7'" outline>
                        {{ props.row.is_active ? "Active" : "Inactive" }}
                    </q-badge>
                </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <q-btn
                        outline
                        no-caps
                        size="sm"
                        color="primary"
                        icon="o_group_add"
                        label="Manage roster"
                        @click="openRoster(props.row)"
                    />
                </q-td>
            </template>

            <template v-slot:no-data>
                <div class="full-width row flex-center q-pa-md text-grey">
                    No managers you can configure.
                </div>
            </template>
        </q-table>

        <q-dialog v-model="dialogOpen">
            <q-card style="min-width: 440px; max-width: 600px;">
                <q-card-section class="row items-center q-pb-none">
                    <div>
                        <div class="text-h6">{{ selectedManagerName }}</div>
                        <div class="text-caption text-grey-7" v-if="selectedManager">
                            {{ roleLabel(selectedManager.role) }}
                            &middot;
                            {{ selectedManager.is_active ? "Active" : "Inactive" }}
                        </div>
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section class="text-caption text-grey-8">
                    Toggle a staff member on to add them to this
                    {{ selectedManager ? roleLabel(selectedManager.role).toLowerCase() : "manager" }}'s
                    roster. Only staff below their level and within your own are shown.
                </q-card-section>

                <q-separator />

                <q-card-section style="max-height: 60vh; overflow-y: auto;">
                    <q-inner-loading :showing="loadingRoster" />

                    <q-input
                        v-if="!loadingRoster && rosterMembers.length"
                        dense
                        outlined
                        v-model="memberFilter"
                        placeholder="Filter staff"
                        class="q-mb-sm"
                    >
                        <template v-slot:append><q-icon name="search" /></template>
                    </q-input>

                    <q-list v-if="!loadingRoster" separator>
                        <q-item v-for="member in filteredMembers" :key="member.id">
                            <q-item-section>
                                <q-item-label>{{ memberName(member) }}</q-item-label>
                                <q-item-label caption>
                                    {{ member.email }} &middot; {{ roleLabel(member.role) }}
                                    &middot; {{ member.is_active ? "Active" : "Inactive" }}
                                </q-item-label>
                            </q-item-section>
                            <q-item-section side>
                                <q-toggle
                                    :model-value="member.assigned"
                                    color="primary"
                                    :disable="savingIds.has(member.id)"
                                    @update:model-value="assigned => toggleMember(member, assigned)"
                                />
                            </q-item-section>
                        </q-item>

                        <q-item v-if="!filteredMembers.length">
                            <q-item-section class="text-grey text-center q-pa-md">
                                No staff available to assign.
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import viewportTablePagination from "@/mixins/viewportTablePagination";
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import { roleLabel } from "@/services/util/roles";

export default {
    name: "StaffAssignmentsPanel",
    mixins: [viewportTablePagination],

    data() {
        return {
            managers: [],
            loadingManagers: false,
            filter: "",
            dialogOpen: false,
            selectedManager: null,
            rosterMembers: [],
            loadingRoster: false,
            memberFilter: "",
            savingIds: new Set(),
            columns: [
                {
                    name: "name",
                    label: "Name",
                    align: "left",
                    field: (row) => `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
                    sortable: true,
                },
                { name: "email", label: "Email", align: "left", field: "email", sortable: true },
                { name: "role", label: "Role", align: "left", field: "role", sortable: true },
                { name: "is_active", label: "Status", align: "left", field: "is_active", sortable: true },
                { name: "member_count", label: "Members", align: "right", field: "member_count", sortable: true },
                { name: "actions", label: "", align: "right", field: "actions" },
            ],
        };
    },

    computed: {
        selectedManagerName() {
            if (!this.selectedManager) return "";
            return `${this.selectedManager.first_name ?? ""} ${this.selectedManager.last_name ?? ""}`.trim();
        },
        filteredMembers() {
            const needle = this.memberFilter.trim().toLowerCase();
            if (!needle) return this.rosterMembers;
            return this.rosterMembers.filter((member) =>
                `${this.memberName(member)} ${member.email ?? ""}`.toLowerCase().includes(needle)
            );
        },
    },

    created() {
        this.fetchManagers();
    },

    methods: {
        roleLabel,

        memberName(member) {
            return `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() || member.email;
        },

        async fetchManagers() {
            this.loadingManagers = true;
            try {
                const response = await api.users.getConfigurableManagers();
                this.managers = response.data ?? [];
            } catch (error) {
                console.error("Error loading managers:", error);
                notify.error("Could not load managers.", "top");
            } finally {
                this.loadingManagers = false;
            }
        },

        async openRoster(manager) {
            this.selectedManager = manager;
            this.rosterMembers = [];
            this.memberFilter = "";
            this.dialogOpen = true;
            this.loadingRoster = true;
            try {
                const response = await api.users.getManagerRoster(manager.id);
                this.rosterMembers = response.data?.members ?? [];
            } catch (error) {
                console.error("Error loading roster:", error);
                notify.error("Could not load this manager's roster.", "top");
                this.dialogOpen = false;
            } finally {
                this.loadingRoster = false;
            }
        },

        async toggleMember(member, assigned) {
            if (this.savingIds.has(member.id)) return;
            this.savingIds.add(member.id);
            try {
                await api.users.updateManagerRoster(this.selectedManager.id, {
                    memberStaffId: member.id,
                    assigned,
                });
                this.rosterMembers = this.rosterMembers.map((candidate) =>
                    candidate.id === member.id ? { ...candidate, assigned } : candidate
                );
                const memberCount = Number(this.selectedManager.member_count) + (assigned ? 1 : -1);
                this.managers = this.managers.map((manager) =>
                    manager.id === this.selectedManager.id
                        ? { ...manager, member_count: memberCount }
                        : manager
                );
                this.selectedManager = { ...this.selectedManager, member_count: memberCount };
                notify.primary(assigned ? "Staff member assigned." : "Staff member removed.", "top");
            } catch (error) {
                console.error("Error updating roster:", error);
                const message = error?.response?.data?.error || error?.response?.data;
                notify.error(typeof message === "string" ? message : "Could not update the roster.", "top");
            } finally {
                this.savingIds.delete(member.id);
            }
        },
    },
};
</script>
